/**
 * Header Bar — 共享 Topbar 组件
 *
 * 砺锋台 + 铸剑阁共用，Ardot v2.0 战术美学：
 * 圆角 0 / 描边 1-2px / monospace / 单色 currentColor
 */

import { audioEngine } from "../services/audio-engine";

export interface HeaderNavItem {
  id: string;
  label: string;
  path: string;
}

export interface HeaderConfig {
  /** 返回按钮目标路径 */
  backPath: string;
  /** 主标题（如 "砺 锋 台"） */
  title: string;
  /** 副标题（如 "FORGE · QUENCH"） */
  subtitle: string;
  /** 右栏状态文字（如 "ONLINE ● SYS:ARMED"），空字符串 = 不显示 */
  statusText?: string;
  /** 导航按钮列表（可选，不传则不渲染导航按钮） */
  navItems?: HeaderNavItem[];
  /** 当前高亮的 nav id */
  activeNavId?: string;
  /** Topbar 容器附加 class（用于页面级样式覆写，如放大标题） */
  topbarClassName?: string;
}

/**
 * 生成共享 header HTML 字符串（hb- 前缀类名）
 * 调用方在 innerHTML 填充后调用 bindHeaderEvents
 */
export function renderHeaderHTML(config: HeaderConfig): string {
  const status = config.statusText
    ? `<span class="hb-status-text">${config.statusText}</span>`
    : "";

  const navBtns = (config.navItems ?? [])
    .map(item => {
      const activeCls = item.id === config.activeNavId ? " hb-nav-btn--active" : "";
      return `<button class="hb-nav-btn${activeCls}" data-hb-nav="${item.id}" data-hb-path="${item.path}">${item.label}</button>`;
    })
    .join("");

  const muted = audioEngine.getMuted();
  const audioBtn = `<button class="hb-btn-audio" id="hb-audio-toggle" title="${muted ? '开启音频' : '关闭音频'}">
    <span class="hb-btn-audio-icon">${muted ? '🔇' : '🔊'}</span>
  </button>`;
  const rightContent = status || navBtns
    ? `${status}${navBtns ? `<div class="hb-nav-group">${navBtns}</div>` : ""}${audioBtn}`
    : audioBtn;

  return /* html */ `
    <div class="hb-topbar${config.topbarClassName ? ` ${config.topbarClassName}` : ""}">
      <div class="hb-left">
        <button class="hb-btn-back" id="hb-back" title="返回">
          <span class="hb-btn-back-arrow">←</span>
          <span class="hb-btn-back-text">返回</span>
        </button>
      </div>
      <div class="hb-center">
        <span class="hb-title">${config.title}</span>
        <span class="hb-subtitle">${config.subtitle}</span>
      </div>
      <div class="hb-right">${rightContent}</div>
    </div>`;
}

/**
 * 绑定 header 事件（innerHTML 后调用一次）
 */
export function bindHeaderEvents(go: (path: string) => void, backPath: string): void {
  document.getElementById("hb-back")?.addEventListener("click", () => {
    go(backPath);
  });

  document.querySelectorAll<HTMLButtonElement>(".hb-nav-btn").forEach(btn => {
    btn.addEventListener("click", () => {
      const path = btn.dataset.hbPath;
      if (path) go(path);
    });
  });

  // V15: 音频开关按钮
  const audioToggle = document.getElementById("hb-audio-toggle");
  const audioIcon = document.querySelector<HTMLSpanElement>(".hb-btn-audio-icon");
  if (audioToggle && audioIcon) {
    audioToggle.addEventListener("click", async () => {
      await audioEngine.init(); // ensure context
      audioEngine.toggleMute();
      const muted = audioEngine.getMuted();
      audioIcon.textContent = muted ? '🔇' : '🔊';
      audioToggle.title = muted ? '开启音频' : '关闭音频';
      audioToggle.classList.toggle('hb-btn-audio--muted', muted);
    });
  }
}
